import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { SECTION_REPORT_CODE } from 'src/app/periodic-review/enums/section-report-code.enum';
import { environment } from 'src/environments/environment';
import { USER_DEPARTMENT_CODE, USER_DEPARTMENT_CODE_COOKIE } from '../constants/user-departement-code.enum';
import { USER_ROLE } from '../constants/user-role.enum';
import { USER_SECTION_CODE_COOKIE } from '../constants/user-section-code.enum';
import { UserBrief } from '../models/user-brief.model';
import { TranslateService } from '../translate';

@Injectable({
    providedIn: 'root'
})
export class LoggedInUserService {
    loggedInUserDetails: UserBrief;
    private ticketClientId = null;
    private clientId = null;
    private userEmail: string = null;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private cookies: CookieService,
        private translateService: TranslateService
    ) {
        this.clientId = this.cookies.get('client-id');
        this.ticketClientId = this.cookies.get('ticket-client-id');
        this.userEmail = this.cookies.get('email');
    }

    storeLoggedInUserDetails(userDetails) {
        this.loggedInUserDetails = userDetails;
        // this.translateService.setLang(userDetails.preferredLanguage?.toLowerCase());
    }

    getLoggedInUserDetails() {
        return this.loggedInUserDetails;
    }

    getLoggedInUserRole() {
        if (this.loggedInUserDetails.roles.length > 1) {
            return this.getHighestRoleLevel(this.loggedInUserDetails);
        } else {
            return this.loggedInUserDetails.roles[0].name as USER_ROLE;
        }
    }

    getHighestRoleLevel(userDetails: UserBrief): USER_ROLE {
        //TEMP: if user has superADMIN, remove
        const index = userDetails.roles.findIndex(i => i.name === USER_ROLE.SUPER_ADMIN);

        let rolesWithSuperAdmin = JSON.parse(JSON.stringify(userDetails));

        if (index !== -1) {
            rolesWithSuperAdmin.roles.splice(index, 1);
        }

        const highestRoleId = Math.min(...rolesWithSuperAdmin.roles.map(item => item.id));
        const highestRoleObj = rolesWithSuperAdmin.roles.find(e => e.id === highestRoleId);
        return highestRoleObj.name as USER_ROLE;
    }

    getLoggedUserId() {
        return this.loggedInUserDetails.id;
    }

    isSuperAdmin() {
        if (isPlatformBrowser(this.platformId)) {
            // if (this.cookies.get('isSuperAdmin')) {
            //     this.cookies.set('userRole', 'ROLE_SUPER_ADMIN', null, '/');
            // }
            return JSON.parse(this.cookies.get('isSuperAdmin') || 'false');
        } else {
            return null;
        }
    }

    getUserRole(): USER_ROLE {
        if (isPlatformBrowser(this.platformId)) {
            //return USER_ROLE.OPERATION_SHIFT_MANAGER;
            return this.cookies.get('userRole') as USER_ROLE;
        } else {
            return null;
        }
    }
    getUserActiveDelegation() {
        if (isPlatformBrowser(this.platformId)) {
            return JSON.parse(this.cookies.get('active-delegation') || 'false');
        } else {
            return null;
        }
    }
    public isViewAs(): boolean {
        return !!this.cookies.get('VIEW_AS');
    }

    public viewAsSection(): string {
        return this.cookies.get('VIEW_AS');
    }
    public getSectionName(): string {
        return this.cookies.get('ClickedSectionFilterPublic');
    }
    public clearViewAs(): void {
        this.cookies.delete('VIEW_AS', '/');
    }

    public isSectionManager(): boolean {
        return this.getUserRole() === USER_ROLE.SECTION_MANAGER;
    }
    public isSectionManagerIC(): boolean {
        return this.getUserRole() == USER_ROLE.SECTION_MANAGER && this.getDepartmentCodeCookie() == USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isSectionManagerNotIC(): boolean {
        return this.getUserRole() == USER_ROLE.SECTION_MANAGER && this.getDepartmentCodeCookie() != USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isShiftManager(): boolean {
        return this.getUserRole() === USER_ROLE.SHIFT_MANAGER;
    }
    public isShiftManagerIC(): boolean {
        return this.getUserRole() === USER_ROLE.SHIFT_MANAGER && this.getDepartmentCodeCookie() == USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isShiftManagerNotIC(): boolean {
        return this.getUserRole() === USER_ROLE.SHIFT_MANAGER && this.getDepartmentCodeCookie() != USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isAccountManager(): boolean {
        return this.getUserRole() === USER_ROLE.ACCOUNT_MANAGER;
    }
    public isRequester(): boolean {
        return this.getUserRole() === USER_ROLE.REQUESTER;
    }
    public isEmployee(): boolean {
        return this.getUserRole() === USER_ROLE.EMPLOYEE;
    }
    public isEmployeeSTSIC(): boolean {
        return this.getUserRole() === USER_ROLE.STS_EMPLOYEE && this.getDepartmentCodeCookie() == USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isEmployeeSTSNotIC(): boolean {
        return this.getUserRole() === USER_ROLE.STS_EMPLOYEE && this.getDepartmentCodeCookie() != USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER;
    }
    public isSTSClientEService(): boolean {
        return this.getUserRole() === USER_ROLE.STS_EMPLOYEE;
    }
    public currentUserSectionId(): string {
        return JSON.stringify(this.loggedInUserDetails.sectionId);
    }
    public currentUserClientId(): string {
        return JSON.stringify(this.clientId || this.loggedInUserDetails?.clientId);
    }
    public isInternalUser(): boolean {
        return this.getUserRole() !== USER_ROLE.REQUESTER && this.getUserRole() !== USER_ROLE.ACCOUNT_MANAGER;
    }

    public isAdminOrSuperAdmin(): boolean {
        return this.isSuperAdmin() || this.getUserRole() === USER_ROLE.ADMIN;
    }

    public isAdminOrManager(): boolean {
        return (
            this.isSuperAdmin() ||
            this.getUserRole() === USER_ROLE.ADMIN ||
            this.getUserRole() === USER_ROLE.SHIFT_MANAGER ||
            this.getUserRole() === USER_ROLE.SECTION_MANAGER
        );
    }
    public isAdmin(): boolean {
        return this.getUserRole() === USER_ROLE.ADMIN;
    }
    public isManager(): boolean {
        return this.getUserRole() === USER_ROLE.SHIFT_MANAGER || this.getUserRole() === USER_ROLE.SECTION_MANAGER;
    }

    public isOperationShiftManager(): boolean {
        return this.getUserRole() === USER_ROLE.OPERATION_SHIFT_MANAGER;
    }

    public isOperationEmployee(): boolean {
        return this.getUserRole() === USER_ROLE.OPERATION_EMPLOYEE;
    }
    public isDirector(): boolean {
        return this.getUserRole() === USER_ROLE.DIRECTOR;
    }
    public isGeneralManager(): boolean {
        return this.getUserRole() === USER_ROLE.GENERAL_MANAGER;
    }

    public isRequesterInquires(): boolean {
        return (
            this.getUserRole() === USER_ROLE.REQUESTER &&
            Boolean(this.clientId || this.loggedInUserDetails?.clientId) &&
            Boolean(!(this.ticketClientId || this.loggedInUserDetails?.ticketClientId))
        );
    }
    public isRequesterEservices(): boolean {
        return (
            this.getUserRole() === USER_ROLE.REQUESTER &&
            Boolean(this.ticketClientId || this.loggedInUserDetails?.ticketClientId) &&
            Boolean(!(this.clientId || this.loggedInUserDetails?.clientId))
        );
    }
    public isRequestBoth(): boolean {
        return (
            this.getUserRole() === USER_ROLE.REQUESTER &&
            Boolean(this.clientId || this.loggedInUserDetails?.clientId) &&
            Boolean(this.ticketClientId || this.loggedInUserDetails?.ticketClientId)
        );
    }
    public isAccountMangerInquires(): boolean {
        return (
            this.getUserRole() === USER_ROLE.ACCOUNT_MANAGER &&
            Boolean(this.clientId || this.loggedInUserDetails?.clientId) &&
            Boolean(!(this.ticketClientId || this.loggedInUserDetails?.ticketClientId))
        );
    }
    public isAccountMangerEservices(): boolean {
        return (
            this.getUserRole() === USER_ROLE.ACCOUNT_MANAGER &&
            Boolean(this.ticketClientId || this.loggedInUserDetails?.ticketClientId) &&
            Boolean(!(this.clientId || this.loggedInUserDetails?.clientId))
        );
    }
    public isAccountMangertBoth(): boolean {
        return (
            this.getUserRole() === USER_ROLE.ACCOUNT_MANAGER &&
            Boolean(this.ticketClientId || this.loggedInUserDetails?.ticketClientId) &&
            Boolean(this.clientId || this.loggedInUserDetails?.clientId)
        );
    }
    public isAccountMangerOrRequester(): boolean {
        return this.isRequester() || this.isAccountManager();
    }
    public isAllUser(): boolean {
        return (
            this.isAccountMangertBoth() ||
            this.isRequestBoth() ||
            this.isRequesterEservices() ||
            this.isRequesterInquires() ||
            this.isAccountMangerEservices() ||
            this.isAccountMangerInquires() ||
            this.isSuperAdmin() ||
            this.isAdmin() ||
            this.isSectionManager() ||
            this.isSectionManagerIC() ||
            this.isOperationEmployee() ||
            this.isOperationShiftManager() ||
            this.isDirector() ||
            this.isGeneralManager() ||
            this.isEmployeeSTSIC() ||
            this.isEmployeeSTSNotIC() ||
            this.isEmployee() ||
            this.isShiftManager() ||
            this.isShiftManagerIC()
        );
    }

    public canSeeDashboard(): boolean {
        return (
            this.isAccountMangerInquires() ||
            this.isAccountMangertBoth() ||
            this.isAdminOrSuperAdmin() ||
            this.isSectionManagerIC() ||
            this.isShiftManagerIC() ||
            this.isGeneralManager()
        );
        // || this.isShiftManagerIC() ||
        // this.isOperationShiftManager();
    }
    public canSeeCallRecordDashboard(): boolean {
        return this.isAdminOrSuperAdmin() || this.isSectionManagerIC() || this.isShiftManagerIC() || this.isGeneralManager();
        // this.isAccountMangerInquires() || this.isAccountMangertBoth() ||

        // || this.isShiftManagerIC() ||
        // this.isOperationShiftManager();
    }

    public isDisplaySeenEmployeeManagement(): boolean {
        return this.isShiftManager() || this.isOperationShiftManager() || this.isAdminOrSuperAdmin() || this.isSectionManager();
    }

    public canAccessMessageCenter(): boolean {
        return (
            ((this.isShiftManager() || this.isEmployee() || this.isSectionManager() || this.isDirector()) &&
                this.getDepartmentCodeCookie() == USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER) ||
            this.isAdminOrSuperAdmin()
        );
    }
    public canAccessReports(): boolean {
        const sectionCodeCookie = this.getSectionCodeCookie();
        const sectionCodes = [
            SECTION_REPORT_CODE.BUSINESS_PROCESS,
            SECTION_REPORT_CODE.LOGISTIC_SUPPORT,
            SECTION_REPORT_CODE.QUALITY_ASSURANCE,
            SECTION_REPORT_CODE.SPECIAL_SERVICES
        ];
        return sectionCodeCookie && sectionCodes.toString().includes(this.getSectionCodeCookie());
    }
    public IsUserRelatedQualityAssurance(): boolean {
        const sectionCodeCookie = this.getSectionCodeCookie();
        const sectionCodes = [SECTION_REPORT_CODE.QUALITY_ASSURANCE];
        return sectionCodeCookie && sectionCodes.toString().includes(this.getSectionCodeCookie());
    }
    public isInternalUsercanAccessReports(): boolean {
        const sectionCodes = [
            SECTION_REPORT_CODE.BUSINESS_PROCESS,
            SECTION_REPORT_CODE.LOGISTIC_SUPPORT,
            SECTION_REPORT_CODE.QUALITY_ASSURANCE,
            SECTION_REPORT_CODE.SPECIAL_SERVICES
        ];
        return (
            (this.getUserRole() === USER_ROLE.DIRECTOR && sectionCodes.toString().includes(this.getDepartmentCodeCookie())) ||
            this.getUserRole() === USER_ROLE.GENERAL_MANAGER ||
            this.isSuperAdmin()
        );
    }
    public cannotSeeInquiries(): boolean {
        return (
            this.isEmployeeSTSNotIC() ||
            this.isShiftManagerNotIC() ||
            this.isSectionManagerNotIC() ||
            this.isDirector() ||
            this.isAccountMangerEservices() ||
            this.isRequesterEservices() ||
            this.isEmployeeSTSIC() // sts not see inquires see service
        );
    }

    allowAccessToLimitedUsersOnProduction() {
        return environment.production !== true || (environment.production === true && environment.alphaUsers?.includes(this.userEmail));
    }

    public canSeeCreateInquiries() {
        return this.isRequestBoth() || this.isRequesterInquires() || this.isAccountMangerInquires() || this.isAccountMangertBoth() || this.isShiftManagerIC();
    }
    getDepartmentCodeCookie() {
        if (isPlatformBrowser(this.platformId)) {
            return this.cookies.get('department-code') as USER_DEPARTMENT_CODE_COOKIE;
        } else {
            return null;
        }
    }
    // getSectionCodeCookie() {
    //     if (isPlatformBrowser(this.platformId)) {
    //         return this.cookies.get('section-code') as SECTION_REPORT_CODE;
    //     } else {
    //         return null;
    //     }
    // }
    getSectionCodeCookie() {
        if (isPlatformBrowser(this.platformId)) {
            return this.cookies.get('section-code') as USER_SECTION_CODE_COOKIE;
        } else {
            return null;
        }
    }
    public isDisplayEServices(): boolean {
        return !this.isRequesterInquires() && !this.isAccountMangerInquires();
    }
    public isDisplayCallRecord(): boolean {
        return (
            !this.isRequester() &&
            !this.isAccountManager() &&
            !this.isDirector() &&
            !this.isEmployeeSTSNotIC() &&
            !this.isShiftManagerNotIC() &&
            !this.isSectionManagerNotIC() &&
            !this.isOperationShiftManager() &&
            !this.isOperationEmployee() &&
            !this.isEmployeeSTSIC()
        );
    }
    public includingDepartmentCode() {
        return [USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER].includes(this.getDepartmentCodeCookie());
    }
    public notIncludingDepartmentCode() {
        return ![USER_DEPARTMENT_CODE_COOKIE.INFORMATION_CENTER].includes(this.getDepartmentCodeCookie());
    }
    public getUserDepartmentCode(): USER_DEPARTMENT_CODE {
        return this.loggedInUserDetails?.departmentCode as USER_DEPARTMENT_CODE;
    }
    public isResolutionTeam(): boolean {
        return this.loggedInUserDetails.rtmemberToSubTicketTypes != undefined && this.loggedInUserDetails.rtmemberToSubTicketTypes.length > 0 ? true : false;
    }

    public isResolutionTeamForSubTicket(subTicketId): boolean {
        return this.loggedInUserDetails?.rtmemberToSubTicketTypes?.length && this.loggedInUserDetails?.rtmemberToSubTicketTypes.some(id => id === subTicketId)
            ? true
            : false;
    }

    public isApprovalUser(): boolean {
        return (
            [
                USER_ROLE.GENERAL_MANAGER,
                USER_ROLE.SECTION_MANAGER,
                USER_ROLE.DIRECTOR,
                USER_ROLE.EMPLOYEE,
                USER_ROLE.SHIFT_MANAGER,
                USER_ROLE.OPERATION_SHIFT_MANAGER,
                USER_ROLE.OPERATION_EMPLOYEE,
                USER_ROLE.STS_EMPLOYEE
            ].includes(this.getUserRole()) || this.isSuperAdmin()
        );
    }
}
